import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import { Box, Alert, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import BasicInfo from './BasicInfo/BasicInfo';
import MemberSelection from './MemberSelection/MemberSelection';

const TabPanel = ({ children, value, tab }) => {
    return value === tab ? <Box sx={{ mt: 2 }}>{children}</Box> : null;
};

const HouseholdForm = (props) => {
    // Use customerLabel from props, or default to 'Customers'
    const customerLabel = props.customerLabel || 'Customers';

    const [currentTab, setCurrentTab] = useState(0);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('error');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Data lists
    const [locList, setLocList] = useState([]);

    // Basic household information state
    const [basicInfo, setBasicInfo] = useState({
        household_name: '',
        household_description: '',
        loc_id: null,
        date_last_visited: null,
        is_actively_serviced: true,
    });

    // Members state
    const [selectedMembers, setSelectedMembers] = useState({
        customers: [],
        contacts: []
    });

    // Validation states
    const [basicInfoValid, setBasicInfoValid] = useState(false);

    // Fetch locations on mount
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axios.get(`${APIURL}/location`, {
                    params: { comp_id: props.user.comp_id }
                });
                setLocList(response.data || []);
            } catch (error) {
                console.error('Error fetching locations:', error);
                setLocList([]);
            }
        };

        if (props.user?.comp_id) {
            fetchLocations();
        }
    }, [props.user?.comp_id]);

    // Fetch household data if editing
    useEffect(() => {
        const fetchHouseholdData = async () => {
            if (!props.edit || !props.household_id) return;

            setIsLoading(true);
            try {
                // Fetch household details
                const householdResponse = await axios.get(
                    `${APIURL}/households/${props.household_id}`
                );
                const household = householdResponse.data;

                console.log('Household data loaded:', household);

                setBasicInfo({
                    household_name: household.household_name || '',
                    household_description: household.household_description || '',
                    loc_id: household.loc_id || null,
                    date_last_visited: household.date_last_visited 
                        ? moment(household.date_last_visited).format('YYYY-MM-DD')
                        : null,
                    is_actively_serviced: household.is_actively_serviced ?? true,
                    date_added: household.date_added
                });

                // Fetch household members
                const membersResponse = await axios.get(
                    `${APIURL}/households/members`,
                    { params: { household_id: props.household_id } }
                );
                const members = membersResponse.data || [];

                console.log('Household members loaded:', members);

                setSelectedMembers({
                    customers: members.filter(m => m.customer_id).map(m => m.customer_id),
                    contacts: members.filter(m => m.contact_id).map(m => m.contact_id)
                });
            } catch (error) {
                console.error('Error fetching household data:', error);
                console.error('Error details:', error.response?.status, error.response?.data);
                setAlertMessage('Failed to load household data');
                setAlertSeverity('error');
                setShowAlert(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHouseholdData();
    }, [props.edit, props.household_id]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // Validate basic info
    useEffect(() => {
        const isValid = basicInfo.household_name.trim().length > 0;
        setBasicInfoValid(isValid);
    }, [basicInfo]);

    // Save household
    const handleSave = async () => {
        // Validate
        if (!basicInfo.household_name.trim()) {
            setAlertMessage('Household name is required');
            setAlertSeverity('error');
            setShowAlert(true);
            setCurrentTab(0);
            return;
        }

        setIsSaving(true);

        try {
            let householdId = props.edit ? props.household_id : null;

            // Save or update household
            if (props.edit) {
                // Update existing household
                const updateResponse = await axios.put(
                    `${APIURL}/households`,
                    {
                        household_id: householdId,
                        household_name: basicInfo.household_name,
                        household_description: basicInfo.household_description,
                        loc_id: basicInfo.loc_id,
                        date_last_visited: basicInfo.date_last_visited,
                        is_actively_serviced: basicInfo.is_actively_serviced,
                    }
                );

                if (updateResponse.data.code !== 200) {
                    throw new Error('Failed to update household');
                }
            } else {
                // Create new household
                const createResponse = await axios.post(
                    `${APIURL}/households`,
                    {
                        household_name: basicInfo.household_name,
                        household_description: basicInfo.household_description,
                        loc_id: basicInfo.loc_id,
                        date_last_visited: basicInfo.date_last_visited,
                        is_actively_serviced: basicInfo.is_actively_serviced,
                        added_by: props.user.emp_id,
                        emp_id: props.user.emp_id,
                        emp_name: `${props.user.emp_first_name} ${props.user.emp_last_name}`,
                        company_id: props.user.comp_id,
                    }
                );

                if (createResponse.data.code !== 200) {
                    throw new Error('Failed to create household');
                }

                householdId = createResponse.data.id;
            }

            // Handle members
            if (householdId) {
                // Get existing members if editing
                let existingMembers = [];
                if (props.edit) {
                    const existingResponse = await axios.get(
                        `${APIURL}/households/members`,
                        { params: { household_id: householdId } }
                    );
                    existingMembers = existingResponse.data || [];
                }

                // Remove members that are no longer selected
                const membersToRemove = existingMembers.filter(member => {
                    if (member.customer_id) {
                        return !selectedMembers.customers.includes(member.customer_id);
                    }
                    if (member.contact_id) {
                        return !selectedMembers.contacts.includes(member.contact_id);
                    }
                    return false;
                });

                for (const member of membersToRemove) {
                    await axios.delete(
                        `${APIURL}/households/members`,
                        { params: { household_member_id: member.household_member_id } }
                    );
                }

                // Add new members
                const existingCustomerIds = existingMembers
                    .filter(m => m.customer_id)
                    .map(m => m.customer_id);
                const existingContactIds = existingMembers
                    .filter(m => m.contact_id)
                    .map(m => m.contact_id);

                // Add new customers
                for (const customerId of selectedMembers.customers) {
                    if (!existingCustomerIds.includes(customerId)) {
                        await axios.post(`${APIURL}/households/members`, {
                            household_id: householdId,
                            customer_id: customerId,
                            added_by: props.user.emp_id,
                            emp_id: props.user.emp_id,
                            company_id: props.user.comp_id,
                        });
                    }
                }

                // Add new contacts
                for (const contactId of selectedMembers.contacts) {
                    if (!existingContactIds.includes(contactId)) {
                        await axios.post(`${APIURL}/households/members`, {
                            household_id: householdId,
                            contact_id: contactId,
                            added_by: props.user.emp_id,
                            emp_id: props.user.emp_id,
                            company_id: props.user.comp_id,
                        });
                    }
                }
            }

            // Success
            setAlertMessage(props.edit ? 'Household updated successfully!' : 'Household created successfully!');
            setAlertSeverity('success');
            setShowAlert(true);

            // Close form after short delay
            setTimeout(() => {
                if (props.onSave) {
                    props.onSave();
                }
                if (props.onClose) {
                    props.onClose();
                }
            }, 1500);

        } catch (error) {
            console.error('Error saving household:', error);
            setAlertMessage(error.response?.data?.message || 'Failed to save household. Please try again.');
            setAlertSeverity('error');
            setShowAlert(true);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
        <Dialog 
            open={props.open} 
            onClose={props.onClose}
            maxWidth="md" 
            fullWidth
            PaperProps={{
                style: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                {props.edit ? 'Edit Household' : 'New Household'}
            </DialogTitle>
            
            <DialogContent>
                {showAlert && (
                    <Alert severity={alertSeverity} onClose={() => setShowAlert(false)} sx={{ mb: 2 }}>
                        {alertMessage}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography>Loading household data...</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={currentTab} onChange={handleTabChange}>
                                <Tab label="Basic Information" />
                                <Tab label="Members" />
                            </Tabs>
                        </Box>

                        <TabPanel value={currentTab} tab={0}>
                            <BasicInfo
                                basicInfo={basicInfo}
                                setBasicInfo={setBasicInfo}
                                locList={locList}
                                user={props.user}
                                edit={props.edit}
                            />
                        </TabPanel>

                        <TabPanel value={currentTab} tab={1}>
                            <MemberSelection
                                selectedMembers={selectedMembers}
                                setSelectedMembers={setSelectedMembers}
                                comp_id={props.user.comp_id}
                                emp_id={props.user.emp_id}
                                emp_type={props.user.type}
                                customerLabel={customerLabel}
                            />
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={props.onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={!basicInfoValid || isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Household'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default HouseholdForm;
